// cartAppearsEmpty() is defined in global.js (shared with cart.js).

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.onDrawerClickBound = this.onDrawerClick.bind(this);
    this.onDrawerSubmitBound = this.onDrawerSubmit.bind(this);
    this.addEventListener('click', this.onDrawerClickBound);
    this.addEventListener('submit', this.onDrawerSubmitBound);
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.bindDrawerActions();
    this.openFromUrlIfNeeded();
  }


  bindDrawerActions() {}

  onDrawerClick(evt) {
    if (evt.target.closest('#CartDrawer-Overlay')) {
      evt.preventDefault();
      evt.stopPropagation();
      this.close();
      return;
    }
    if (evt.target.closest('[data-cart-drawer-continue]')) {
      evt.preventDefault();
      evt.stopPropagation();
      this.close();
      return;
    }
    if (evt.target.closest('#CartDrawer-Discount-Apply')) {
      evt.preventDefault();
      this.applyDiscount();
    }
  }

  onDrawerSubmit(evt) {
    const form = evt.target;
    if (!form.matches || !form.matches('[data-cart-drawer-cross-sell-form]')) return;
    evt.preventDefault();
    this.submitCrossSellForm(form);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const notePanel = cartDrawerNote.nextElementSibling;
    if (notePanel && notePanel.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', notePanel.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  applyDiscount() {
    const input = this.querySelector('#CartDrawer-Discount-Input');
    const messageEl = this.querySelector('#CartDrawer-Discount-Message');
    const applyBtn = this.querySelector('#CartDrawer-Discount-Apply');
    if (!input || !messageEl) return;

    const code = input.value.trim();
    if (!code) {
      messageEl.textContent =
        (window.cartDrawerStrings && window.cartDrawerStrings.discountCodeRequired) || '';
      messageEl.classList.remove('visually-hidden');
      messageEl.classList.remove('cart-drawer__discount-message--success');
      messageEl.classList.add('cart-drawer__discount-message--error');
      input.setAttribute('aria-invalid', 'true');
      return;
    }

    messageEl.classList.add('visually-hidden');
    messageEl.textContent = '';
    messageEl.classList.remove('cart-drawer__discount-message--success', 'cart-drawer__discount-message--error');
    input.removeAttribute('aria-invalid');

    const sectionsPayload = this.getSectionsToRender().map((section) => section.id);
    const body = JSON.stringify({
      discount: code,
      sections: sectionsPayload,
      sections_url: window.location.pathname
    });

    if (applyBtn) {
      applyBtn.setAttribute('aria-disabled', 'true');
      applyBtn.classList.add('loading');
    }

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((stateText) => {
        let parsedState;
        try {
          parsedState = JSON.parse(stateText);
        } catch (parseErr) {
          console.error(parseErr);
          throw new Error('cart-discount-parse');
        }

        const showDiscountError = (text) => {
          messageEl.textContent = text;
          messageEl.classList.remove('visually-hidden', 'cart-drawer__discount-message--success');
          messageEl.classList.add('cart-drawer__discount-message--error');
          input.setAttribute('aria-invalid', 'true');
        };

        if (parsedState.status && Number(parsedState.status) === 422) {
          showDiscountError(
            parsedState.description ||
              (window.cartDrawerStrings && window.cartDrawerStrings.discountApplyError) ||
              ''
          );
          return;
        }

        const normalizedCode = code.toLowerCase();
        const codeEntry =
          parsedState.discount_codes &&
          parsedState.discount_codes.find(
            (dc) =>
              dc &&
              typeof dc.code === 'string' &&
              dc.code.toLowerCase() === normalizedCode
          );
        if (codeEntry && codeEntry.applicable === false) {
          showDiscountError(
            (window.cartDrawerStrings && window.cartDrawerStrings.discountApplyError) || ''
          );
          return;
        }

        if (typeof parsedState.item_count === 'number') {
          this.classList.toggle('is-empty', cartAppearsEmpty(parsedState));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount' });
        this.renderContents(parsedState);

        messageEl.classList.add('visually-hidden');
        messageEl.textContent = '';
        input.removeAttribute('aria-invalid');
        input.value = '';
      })
      .catch((e) => {
        console.error(e);
        messageEl.textContent =
          (window.cartDrawerStrings && window.cartDrawerStrings.discountApplyError) ||
          (window.cartStrings && window.cartStrings.error) ||
          '';
        messageEl.classList.remove('visually-hidden');
        messageEl.classList.remove('cart-drawer__discount-message--success');
        messageEl.classList.add('cart-drawer__discount-message--error');
        input.setAttribute('aria-invalid', 'true');
      })
      .finally(() => {
        if (applyBtn) {
          applyBtn.removeAttribute('aria-disabled');
          applyBtn.classList.remove('loading');
        }
      });
  }

  submitCrossSellForm(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    let errEl = form.querySelector('[data-cross-sell-error]');
    if (errEl) {
      errEl.textContent = '';
      errEl.classList.add('visually-hidden');
    }

    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);

    fetch(`${routes.cart_add_url}`, { ...config, body: formData })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const desc = response.description || response.message || '';
          console.error('Cart drawer cross-sell:', desc);
          if (!errEl) {
            errEl = document.createElement('p');
            errEl.className = 'cart-drawer__cross-sell-error caption';
            errEl.setAttribute('data-cross-sell-error', '');
            form.appendChild(errEl);
          }
          errEl.textContent = desc || (window.cartStrings && window.cartStrings.error) || '';
          errEl.classList.remove('visually-hidden');
          return;
        }

        if (typeof response.item_count === 'number') {
          this.classList.toggle('is-empty', cartAppearsEmpty(response));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-cross-sell' });
        this.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
        if (!errEl) {
          errEl = document.createElement('p');
          errEl.className = 'cart-drawer__cross-sell-error caption';
          errEl.setAttribute('data-cross-sell-error', '');
          form.appendChild(errEl);
        }
        errEl.textContent = (window.cartStrings && window.cartStrings.error) || '';
        errEl.classList.remove('visually-hidden');
      })
      .finally(() => {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('loading');
        }
      });
  }

  openFromUrlIfNeeded() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_cart_drawer') === '1') {
      params.delete('open_cart_drawer');
      const qs = params.toString();
      const cleanUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', cleanUrl);
      this.open(document.querySelector('#cart-icon-bubble') || undefined);
      return;
    }
    if (window.location.hash === '#cart-drawer') {
      window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
      this.open(document.querySelector('#cart-icon-bubble') || undefined);
    }
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', cartAppearsEmpty(parsedState));
    }

    setTimeout(() => {
      this.bindDrawerActions();
      this.open();
      if (window.ShippingInsurance && typeof window.ShippingInsurance.sync === 'function') {
        window.ShippingInsurance.sync(true);
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /**
   * Drawer line items are updated via Section Rendering (renderContents, /cart/change).
   * The base implementation fetches main-cart-items and would replace drawer markup incorrectly.
   */
  onCartUpdate() {}

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
